import React, { useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '../shared/contexts/AuthContext';
import { PermissionProvider } from '../shared/contexts/PermissionContext';
import { AuthGuard } from '../shared/guards/AuthGuard';
import { NoAuthGuard } from '../shared/guards/NoAuthGuard';
import { TokenManager } from '../shared/utils/token-manager';
import Layout from '../shared/components/Layout';
import { SignIn } from '../features/auth/components/SignIn';
import { SignUp } from '../features/auth/components/SignUp';
import { Dashboard } from '../features/dashboard/components/Dashboard';
import { Users } from '../features/users/components/Users';
import { UserDetail } from '../features/users/components/userDetail';
import { Profile } from '../features/profile/components/Profile';
import { ChangePassword } from '../features/profile/components/ChangePassword';
import { Settings } from '../features/settings/components/Settings';
import { Error404 } from '../features/errors/components/Error404';
import { Error500 } from '../features/errors/components/Error500';
import { Loader } from '../shared/components/Loader';
import { AddEditAdmin } from '../features/admin/components/AddEditAdmin';
import { AddEditUser } from '../features/users/components/AddEditUser';
import { Admin } from '../features/admin/components/Admin';
import { Category } from '../features/category/components/Category';
import { CategoryValuesList } from '../features/category/components/CategoryValuesList';
import { Product } from '../features/product/components/Product';
import { ProductDetail } from '../features/product/components/ProductDetail';
import { BulkImageUpload } from '../features/product/components/BulkImageUpload';
import { Coupon } from '../features/coupon/components/Coupon';
import { AddEditCoupon } from '../features/coupon/components/AddEditCoupon';
import { CmsPages } from '../features/cms/components/CmsPages';
import { ViewCmsPage } from '../features/cms/components/ViewCmsPage';
import { EditCmsPage } from '../features/cms/components/EditCmsPage';
import { ProductRating } from '../features/product-rating/components/ProductRating';
import { Notifications } from '../features/notifications/components/Notifications';
import { SendNotification } from '../features/notifications/components/SendNotification';
import { Stock } from '../features/stock/components/Stock';
import { StockDetail } from '../features/stock/components/StockDetail';
import { Tax } from '../features/tax/components/Tax';
import { DeliveryCharge } from '../features/delivery-charge/components/DeliveryCharge';
import { AddEditDeliveryCharge } from '../features/delivery-charge/components/AddEditDeliveryCharge';
import { CategoryValue } from '../features/category-value/components/CategoryValue';
import { Order } from '../features/order/components/Order';
import { OrderDetail } from '../features/order/components/OrderDetail';
import { Payments } from '../features/payment/components/Payments';
import { DeliveryProof } from '../features/delivery-proof/components/DeliveryProof';
import { GoFrugalSync } from '../features/gofrugal-sync/components/GoFrugalSync';
import { GoFrugalErrorLogs } from '../features/gofrugal-log/components/GoFrugalErrorLogs';
import { Banner } from '../features/banner/components/Banner';
import { Collection, CollectionProducts, AddEditCollection } from '../features/collection';


const AppContent: React.FC = () => {
    const { handleToken, check, isInitializing, authenticated } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const processToken = async () => {
            const urlParams = new URLSearchParams(location.search);
            let token = urlParams.get('token');

            if (!token && location.hash) {
                const hashParams = new URLSearchParams(location.hash.split('?')[1] || '');
                token = hashParams.get('token');
            }

            if (token) {
                try {
                    await handleToken(token);
                    TokenManager.cleanupTokenFromUrl();
                    const isAuth = await check();
                    if (isAuth) {
                        // Avoid full page reload, just navigate
                        if (location.pathname === '/sign-in' || location.pathname === '/') {
                            navigate('/dashboard', { replace: true });
                        }
                    } else {
                        navigate('/sign-in', { replace: true });
                    }
                } catch (error) {
                    console.error('Error processing token:', error);
                    TokenManager.cleanupTokenFromUrl();
                    navigate('/sign-in', { replace: true });
                }
            } else {
                check();
            }
        };

        processToken();
    }, [location.search, location.hash, handleToken, check, navigate]);

    if (isInitializing) {
        return <Loader fullScreen={true} />;
    }

    return (
        <Routes>
            {/* Redirect empty path based on auth status */}
            <Route path="/" element={
                authenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/sign-in" replace />
            } />
            <Route path="/signed-in-redirect" element={<Navigate to="/dashboard" replace />} />

            {/* Auth routes for guests */}
            <Route element={<NoAuthGuard><Layout layout="empty" /></NoAuthGuard>}>
                <Route path="/sign-in" element={<SignIn />} />
                <Route path="/sign-up" element={<SignUp />} />
            </Route>

            {/* ADMIN ROUTES */}
            <Route element={<AuthGuard><Layout layout="classic" /></AuthGuard>}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/add" element={<AddEditAdmin />} />
                <Route path="/admin/edit/:id" element={<AddEditAdmin />} />
                <Route path="/users" element={<Users />} />
                <Route path="/users/view/:id" element={<UserDetail />} />
                <Route path="/users/add" element={<AddEditUser />} />
                <Route path="/users/edit/:id" element={<AddEditUser />} />
                <Route path="/category" element={<Category />} />
                <Route path="/category/:categoryName" element={<CategoryValuesList />} />
                <Route path="/category-value" element={<CategoryValue />} />
                <Route path="/product" element={<Product />} />
                <Route path="/product/view/:id" element={<ProductDetail />} />
                <Route path="/product/bulk-image-upload" element={<BulkImageUpload />} />
                <Route path="/stock" element={<Stock />} />
                <Route path="/stock/view/:id" element={<StockDetail />} />
                <Route path="/product-ratings" element={<ProductRating />} />
                <Route path="/coupons" element={<Coupon />} />
                <Route path="/coupons/add" element={<AddEditCoupon />} />
                <Route path="/coupons/edit/:id" element={<AddEditCoupon />} />
                <Route path="/cms" element={<CmsPages />} />
                <Route path="/cms/view/:slug" element={<ViewCmsPage />} />
                <Route path="/cms/edit/:slug" element={<EditCmsPage />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/notifications/send" element={<SendNotification />} />
                <Route path="/banners" element={<Banner />} />
                <Route path="/collections" element={<Collection />} />
                <Route path="/collections/add" element={<AddEditCollection />} />
                <Route path="/collections/edit/:id" element={<AddEditCollection />} />
                <Route path="/collections/:id/products" element={<CollectionProducts />} />
                <Route path="/tax" element={<Tax />} />
                <Route path="/delivery-charge" element={<DeliveryCharge />} />
                <Route path="/delivery-charge/add" element={<AddEditDeliveryCharge />} />
                <Route path="/delivery-charge/edit/:id" element={<AddEditDeliveryCharge />} />
                <Route path="/orders" element={<Order />} />
                <Route path="/orders/view/:id" element={<OrderDetail />} />
                <Route path="/payments" element={<Payments />} />
                <Route path="/delivery-proof" element={<DeliveryProof />} />
                <Route path="/gofrugal-sync" element={<GoFrugalSync />} />
                <Route path="/gofrugal-error-logs" element={<GoFrugalErrorLogs />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/change-password" element={<ChangePassword />} />
            </Route>

            {/* Error pages */}
            <Route path="/404-not-found" element={<Error404 />} />
            <Route path="/500" element={<Error500 />} />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/404-not-found" replace />} />
        </Routes>
    );
};

const App: React.FC = () => {
    return (
        <HashRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <AuthProvider>
                <PermissionProvider>
                    <AppContent />
                </PermissionProvider>
            </AuthProvider>
        </HashRouter>
    );
};


export default App;
